import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { AppIconComponent } from '../../../common/components/icon/icon.component';
import {
  InsuranceCategory,
  InsuranceTypeDto,
} from '../../../core/data/dtos/insurance-type.dto';
import {
  NgbModal,
  NgbModalModule,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { ActivatedRoute, Router } from '@angular/router';
import { InsuranceTypesService } from '../services/insurance-types.service';
import { CreateQuoteRequestDto } from '../../../core/data/dtos/create-quote-request.dto';
import { QuoteDetailCreateDto } from '../../../core/data/dtos/quote-detail-create.dto';
import { QuotesService } from '../../../core/data/services/quotes.service';
import { QuoteResponseDto } from '../../../core/data/dtos/quote-response.dto';
import { lastValueFrom } from 'rxjs';
import { OptionModalComponent } from './components/option-modal/option-modal.component';
import getSafeIconName from '../../../common/functions/get-safe-icon-name';
import { Gender } from '../../../core/data/enums/gender.enum';
import { UpdateQuoteRequestDto } from '../../../core/data/dtos/update-quote-request.dto';
import { QuoteDetailUpsertDto } from '../../../core/data/dtos/quote-detail-upsert.dto';
import { QuoteCoverageRequestDto } from '../../../core/data/dtos/quote-coverage-request.dto';
import { QuoteInsuredPersonUpsertDto } from '../../../core/data/dtos/quote-insured-person-upsert.dto';
import { SwalService } from '../../../common/services/swal.service';
import { SpecialRequestModalComponent, SpecialRequestData, AffectedInsurance } from './components/special-request-modal/special-request-modal.component';
import { SpecialRequestsService } from '../../../core/data/services/special-requests.service';
import { toIsoDate } from '../../../common/utils/date-utils';
import { formatDescription } from '../../../common/utils/format-description';
import { ExchangeRateService } from '../../../core/data/services/exchange-rate.service';
import { AuthService } from '../../auth/services/auth.service';
import {
  normalizeRelation,
  resolveSelectedPlanId,
  mapCoveragesFromForm,
  mapInsuredPeopleFromForm,
  mapAnswersFromForm,
  normalizeProducerCode,
  denormalizeProducerCode,
  feetInchesToCm,
  weightToLb,
} from '../../../common/utils/quote-detail-mappers';

type PopoverCtor = typeof import('bootstrap/js/dist/popover.js').default;
type PopoverInstance = import('bootstrap/js/dist/popover.js').default;

@Component({
  selector: 'app-insurance-types',
  standalone: true,
  imports: [AppIconComponent, NgbModalModule, NgSelectModule],
  templateUrl: './insurance-types.component.html',
  styleUrl: './insurance-types.component.css',
})
export class InsuranceTypesComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy {
  private router = inject(Router);

  @ViewChildren('infoButton', { read: ElementRef }) infoButtons!: QueryList<
    ElementRef<HTMLButtonElement>
  >;

  /**
   * Ajusta este valor para decidir qué renglón se muestra en la vista.
   */
  filterCategory: InsuranceCategory = InsuranceCategory.Life;

  insuranceOptions: InsuranceTypeDto[] = [];
  isLoading = true;

  // Quote creation state
  selectedTypeIds = new Set<number>();
  quoteRequest: CreateQuoteRequestDto = {
    insuranceTypes: [],
  };

  /**
   * Store questionnaire answers per insurance type ID.
   * Key: insurance type ID, Value: object with questionId -> answer value.
   */
  questionAnswersMap = new Map<number, Record<string, string | boolean | null>>();

  private popoverCtor?: PopoverCtor;
  private readonly popovers = new Map<HTMLElement, PopoverInstance>();
  private modalRef: NgbModalRef | null = null;
  readonly intermediaryOptions: Array<{
    id: string;
    name: string;
    registration: string;
  }> = [
      {
        id: 'int-001',
        name: 'Intermediarios Colonial',
        registration: 'REG-4581',
      },
      {
        id: 'int-002',
        name: 'Agentes Gómez & Asociados',
        registration: 'REG-8722',
      },
      {
        id: 'int-003',
        name: 'Consultores Vida Caribe',
        registration: 'REG-9914',
      },
    ];

  constructor(
    private readonly modalService: NgbModal,
    private readonly route: ActivatedRoute,
    private readonly insuranceTypesService: InsuranceTypesService,
    private readonly quotesService: QuotesService,
  ) { }

  private readonly swal = inject(SwalService);
  private readonly specialRequestsService = inject(SpecialRequestsService);
  private readonly exchangeRateService = inject(ExchangeRateService);
  private readonly authService = inject(AuthService);

  ngOnInit(): void {
    // Initialize from current quote in session if available
    this.initializeFromCurrentQuote();

    // Read renglon from query params if provided and valid
    this.route.queryParamMap.subscribe((params) => {
      const categoryParam = params.get('category');
      let category: InsuranceCategory | null = null;

      if (categoryParam) {
        // Find if categoryParam matches any enum value
        const found = Object.values(InsuranceCategory).find(v => v.toLowerCase() === categoryParam.toLowerCase());
        if (found) {
          category = found as InsuranceCategory;
        }
      }

      if (category !== null) {
        this.filterCategory = category;
      }

      void this.fetchInsuranceTypes();
    });
  }

  private initializeFromCurrentQuote(): void {
    const current = this.quotesService.currentQuote;
    if (!current) return;

    // Infer category from first detail if available and not overridden by query params later
    if (current.details && current.details.length > 0) {
      const first = current.details[0];
      if (first.insuranceType?.category) {
        this.filterCategory = first.insuranceType.category;
      }
    }

    // Map top-level fields
    this.quoteRequest.producerCode = normalizeProducerCode(current.producerCode);
    this.quoteRequest.retailer = current.retailer ?? (null as any);
    this.quoteRequest.supervisor = current.supervisor ?? (null as any);
    this.quoteRequest.manager = current.manager ?? (null as any);
    this.quoteRequest.branch = (current.branch ?? undefined) as any;
    this.quoteRequest.quoteEmail =
      current.quoteEmail ?? current.contactEmail ?? null;

    const mappedDetails: QuoteDetailCreateDto[] = (current.details ?? []).map(
      (d) => {
        const currency = d.currency ?? null;
        return {
          typeId: d.typeId,
          selectedPlanId: d.selectedPlanId,
          durationYears: d.durationYears,
          currency,
          coverages: (d.coverages ?? []).map((c) => ({
            // Use the underlying coverage relation id for form selection; fallback to existing id
            id: (c.coverage?.id as any) ?? null,
            coverageId: c.coverage?.id ?? c.coverageId ?? null,
            coverageCode: c.coverageCode ?? c.coverage?.coverageCode ?? null,
            name: (c.coverage?.name as any) ?? (c.name as any) ?? null,
            description:
              (c.coverage?.description as any) ??
              (c.description as any) ??
              null,
            amount: c.amount,
            currency,
          })),
          insuredPeople: (d.insuredPeople ?? []).map((ip) => ({
            id: ip.id ?? null,
            personId: ip.personId ?? null,
            isPrimary: !!ip.isPrimary,
            contactEmail: ip.contactEmail ?? null,
            homePhone: ip.homePhone ?? null,
            mobilePhone: ip.mobilePhone ?? null,
            person: ip.person ? {
              id: (ip.person as any).id ?? null,
              name: ip.person.name ?? null,
              lastName: ip.person.lastName ?? null,
              documentType: (ip.person as any).documentType ?? null,
              documentNumber: ip.person.documentNumber ?? null,
              documentExpiresAt: (ip.person as any).documentExpiresAt ?? null,
              birthDate: ip.person.birthDate ?? null,
              gender: ip.person.gender ?? null,
              civilState: (ip.person as any).civilState ?? null,
              heightCm: (ip.person as any).heightCm ?? null,
              weightLb: (ip.person as any).weightLb ?? null,
              nacionality: (ip.person as any).nacionality ?? null,
              personAddressId: (ip.person as any).personAddressId ?? null,
              isPep: (ip.person as any).isPep ?? null,
              occupation: (ip.person as any).occupation ?? null,
              primaryInsuredRelation: (ip.person as any).primaryInsuredRelation ?? (ip.person as any).relationship ?? null,
              relationship: (ip.person as any).relationship ?? (ip.person as any).primaryInsuredRelation ?? null,
              address: (ip.person as any).address ?? null,
            } : undefined,
          })),
          answers: (d.answers ?? []).map((a) => ({
            id: a.id ?? null,
            questionId: a.questionId ?? null,
            value: a.value ?? null,
          })),
        } as QuoteDetailCreateDto;
      },
    );

    // Enforce single selection: take only the first detail
    const singleDetail = mappedDetails.length > 0 ? [mappedDetails[0]] : [];
    this.quoteRequest.insuranceTypes = singleDetail;

    // Pre-mark selected types based on the single detail
    this.selectedTypeIds = new Set<number>(
      singleDetail
        .map((md) => md.typeId)
        .filter((v): v is number => typeof v === 'number'),
    );
  }

  private async fetchInsuranceTypes(): Promise<void> {
    this.isLoading = true;
    this.swal.showLoading('Cargando opciones de seguro...');
    try {
      // Wait for auth/redirect handling so a logged-in retailer's bankId is available
      await this.authService.ready;
      const categoryId = this.mapCategoryToId(this.filterCategory);
      const types = await lastValueFrom(
        this.insuranceTypesService.getInsuranceTypes(categoryId),
      );
      this.insuranceOptions = types ?? [];
    } catch {
      this.insuranceOptions = [];
    } finally {
      this.isLoading = false;
      this.swal.dismiss();
    }
  }

  private mapCategoryToId(category: InsuranceCategory): string {
    switch (category) {
      case InsuranceCategory.Life:
        return 'life';
      case InsuranceCategory.Health:
        return 'health';
      case InsuranceCategory.Accident:
        return 'accident';
      default:
        return 'other';
    }
  }

  getCategoryLabel(category: InsuranceCategory): string {
    switch (category) {
      case InsuranceCategory.Life:
        return 'Seguro de Vida';
      case InsuranceCategory.Health:
        return 'Seguro de Salud';
      case InsuranceCategory.Accident:
        return 'Seguro de Accidentes Personales';
      default:
        return 'Seguro';
    }
  }

  ngAfterViewInit(): void {
    void this.ensurePopovers();
  }

  ngAfterViewChecked(): void {
    void this.ensurePopovers();
  }

  ngOnDestroy(): void {
    this.detachPopovers();
    this.modalRef?.dismiss();
    this.modalRef = null;
  }

  onInfoClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const button = event.currentTarget as HTMLButtonElement | null;
    button?.focus();
  }

  onBack() {
    this.router.navigate(['/']);
  }

  openOptionModal(option: InsuranceTypeDto): void {
    if (this.modalRef) {
      return;
    }

    this.modalRef = this.modalService.open(OptionModalComponent, {
      centered: true,
      size: 'lg',
      scrollable: true,
      backdrop: 'static',
      keyboard: false,
      modalDialogClass: 'modal-insurace-option',
    });

    const instance = this.modalRef.componentInstance as any;
    if (instance) {
      instance.option = option;
      instance.intermediaryOptions = this.intermediaryOptions;
      instance.selected = this.selectedTypeIds.has(option.id as number);
      instance.canDelete = this.selectedTypeIds.size > 1;
      // Pass existing saved detail (if any) and current producer code for prefill
      const existing = (this.quoteRequest.insuranceTypes || []).find(
        (d) => d.typeId === option.id,
      );
      if (existing) {
        instance.initialDetail = existing;
      }
      const normalizedPC = normalizeProducerCode(this.quoteRequest.producerCode);
      if (normalizedPC) {
        instance.initialProducerCode = normalizedPC;
      }

      // Calculate other selected details for currency validation and conversion
      const otherSelectedDetails = (this.quoteRequest.insuranceTypes || [])
        .filter((d) => d.typeId !== option.id)
        .map((d) => ({
          detail: d,
          insuranceType: this.insuranceOptions.find((opt) => opt.id === d.typeId)!,
        }))
        .filter((item) => !!item.insuranceType);
      instance.otherSelectedDetails = otherSelectedDetails;

      // Pass the current global currency if valid to show warnings
      if (this.quoteRequest.insuranceTypes?.length) {
        const globalCurrency = this.quoteRequest.insuranceTypes[0].currency;
        instance.currentGlobalCurrency = globalCurrency;

        // Determine if the option supports the current global currency
        const supportedCurrencies = (option.currencies || []).map(c => c.currency);
        if (globalCurrency && supportedCurrencies.includes(globalCurrency)) {
          instance.initialCurrency = globalCurrency;
        }
      }
    }

    this.modalRef.result
      .then((result: any) => {
        // result comes from activeModal.close in modal (on save)
        if (result && result.option && result.formValue) {
          this.onModalSave(
            result.option as InsuranceTypeDto,
            result.formValue,
            result.otherSelectedDetails,
          );
        }
        this.modalRef = null;
      })
      .catch((reason: any) => {
        // reason may be 'remove' to unselect the type
        if (reason === 'remove') {
          this.unselectInsuranceType(option);
        }
        this.modalRef = null;
      });
  }

  closeOptionModal(reason: string = 'manual-close'): void {
    this.modalRef?.close(reason);
  }

  private onModalSave(
    option: InsuranceTypeDto,
    formValue: any,
    otherSelectedDetails?: any[],
  ): void {
    // Set or clear producerCode globally based on intermediary selection
    if (formValue?.intermediaryEnabled && formValue?.intermediary) {
      this.quoteRequest.producerCode = String(formValue.intermediary);
    } else {
      this.quoteRequest.producerCode = null;
    }

    // Build and upsert detail for this insurance type (for the request body flow)
    const existing = (this.quoteRequest.insuranceTypes || []).find(
      (d) => d.typeId === option.id,
    );
    const detail = this.buildDetailFromForm(option, formValue, existing);
    this.upsertDetail(detail);

    // Also update the in-session current quote detail so state is consistent regardless of screen
    this.updateCurrentQuoteSessionFromModal(option, formValue);

    // Extract and store questionnaire answers
    if (option?.id != null && formValue) {
      const answers: Record<string, string | boolean | null> = {};
      for (const key of Object.keys(formValue)) {
        if (key.startsWith('question_')) {
          answers[key] = formValue[key];
        }
      }
      this.questionAnswersMap.set(option.id, answers);
    }

    // Mark as selected
    if (option?.id != null) {
      this.selectedTypeIds.add(option.id);
    }

    // Handle currency change for other selected insurances using the modal's conversion
    const newCurrency = formValue?.currency;
    if (newCurrency && otherSelectedDetails) {
      const updatedOtherDetails = otherSelectedDetails.map((item) => item.detail);
      const updatedTypeIds = new Set(updatedOtherDetails.map((d) => d.typeId));

      this.quoteRequest.insuranceTypes = (
        this.quoteRequest.insuranceTypes ?? []
      ).map((t) => {
        if (t.typeId === option.id) return t;
        if (updatedTypeIds.has(t.typeId)) {
          return updatedOtherDetails.find((ud) => ud.typeId === t.typeId);
        }
        return t;
      });

      const cur = this.quotesService.currentQuote;
      if (cur && cur.details) {
        const newDetails = cur.details.map((d: any) => {
          if (d.typeId === option.id) return d;
          if (updatedTypeIds.has(d.typeId)) {
            const converted = updatedOtherDetails.find(
              (ud) => ud.typeId === d.typeId,
            );
            return {
              ...d,
              currency: newCurrency,
              coverages: (d.coverages ?? []).map((c: any) => {
                const convertedCov = (converted.coverages ?? []).find(
                  (cc: any) => (cc.id || cc.coverageId) === (c.id || c.coverageId || c.coverage?.id),
                );
                return {
                  ...c,
                  currency: newCurrency,
                  amount: convertedCov ? convertedCov.amount : c.amount,
                };
              }),
            };
          }
          return d;
        });
        this.quotesService.setCurrentQuoteLocal({
          ...cur,
          details: newDetails,
        });
      }
    }
  }

  private updateCurrentQuoteSessionFromModal(
    option: InsuranceTypeDto,
    formValue: any,
  ): void {
    const cur = this.quotesService.currentQuote;
    const typeId = option.id as number;
    if (!cur || !typeId) return;

    const details = [...(cur.details ?? [])];
    const existingIdx = details.findIndex(
      (d) => (d?.typeId as number) === typeId,
    );
    const existing = existingIdx >= 0 ? (details[existingIdx] as any) : null;

    // Base updated detail from existing or a new stub
    const updated: any = existing
      ? { ...existing }
      : { typeId, insuranceType: option };

    // Map currency and duration (modal uses months)
    const durationMonths = Number(formValue?.duration ?? 0);
    updated.durationYears = Math.max(0, Math.ceil(durationMonths / 12));
    if (formValue?.currency) {
      updated.currency = formValue.currency;
    }

    // Coverages using shared mapper
    updated.coverages = mapCoveragesFromForm(option, formValue, existing?.coverages ?? []);

    // Insured people using shared mapper
    updated.insuredPeople = mapInsuredPeopleFromForm(formValue, existing?.insuredPeople ?? []);
    // Convert birth dates to ISO for session storage
    updated.insuredPeople.forEach((ip: any) => {
      if (ip?.person?.birthDate) {
        ip.person.birthDate = toIsoDate(ip.person.birthDate);
      }
    });

    updated.planOption = formValue?.planOption ?? null;

    // Persist health questionnaire answers into the detail
    const answersOut = mapAnswersFromForm(formValue, existing?.answers ?? []);
    if (answersOut.length > 0) {
      updated.answers = answersOut;
    }

    const newDetails = [updated];

    this.quotesService.setCurrentQuoteLocal({
      ...cur,
      producerCode: this.quoteRequest.producerCode ?? null,
      details: newDetails,
    });
  }

  private unselectInsuranceType(option: InsuranceTypeDto): void {
    if (option?.id == null) return;
    this.selectedTypeIds.delete(option.id);
    const list = this.quoteRequest.insuranceTypes ?? [];
    this.quoteRequest.insuranceTypes = list.filter(
      (d) => d.typeId !== option.id,
    );
  }

  private buildDetailFromForm(
    option: InsuranceTypeDto,
    formValue: any,
    existing?: QuoteDetailCreateDto,
  ): QuoteDetailCreateDto {
    const typeId = option.id as number;

    // Duration comes in months from modal; convert to years (ceil)
    const durationMonths = Number(formValue?.duration ?? 0);
    const durationYears = Math.max(0, Math.ceil(durationMonths / 12));

    const currency: string | null = formValue?.currency ?? null;

    // Build coverages using shared mapper, then enrich with currency for the request DTO
    const rawCoverages = mapCoveragesFromForm(option, formValue);
    const coverages: QuoteDetailCreateDto['coverages'] = rawCoverages.map((c: any) => ({
      id: c.coverageId ?? c.coverage?.id ?? null,
      coverageId: c.coverageId ?? null,
      coverageCode: c.coverageCode ?? c.coverage?.coverageCode ?? null,
      name: c.name ?? null,
      description: c.description ?? null,
      amount: c.amount ?? 0,
      currency,
    }));

    // Insured people mapping with gender from form
    const insuredPeople: QuoteDetailCreateDto['insuredPeople'] = [];

    // Find existing primary to preserve contact info
    const existingPrimary = existing?.insuredPeople?.find(p => !!p.isPrimary);

    const primaryGender: Gender | null =
      (formValue?.primaryGender as Gender) ?? null;
    insuredPeople.push({
      person: {
        birthDate: toIsoDate(formValue?.birthDate) ?? null,
        gender: primaryGender,
        occupation:
          formValue?.occupationCode != null
            ? String(formValue.occupationCode)
            : null,
        heightCm: feetInchesToCm(formValue?.heightFeet, formValue?.heightInches)
          ?? existingPrimary?.person?.heightCm ?? null,
        weightLb: weightToLb(formValue?.weight, formValue?.weightUnit)
          ?? existingPrimary?.person?.weightLb ?? null,
        // Preserve address if exists
        address: existingPrimary?.person?.address ?? null,
      },
      isPrimary: true,
      // Preserve contact info
      contactEmail: existingPrimary?.contactEmail ?? null,
      homePhone: existingPrimary?.homePhone ?? null,
      mobilePhone: existingPrimary?.mobilePhone ?? null,
    });

    // If family is allowed and spouse selected, add spouse with relation 'Conyuge'
    if (option?.familyAllowed && formValue?.spouseIncluded) {
      const spouseGender: Gender | null =
        (formValue?.spouseGender as Gender) ?? null;
      insuredPeople.push({
        person: {
          birthDate: toIsoDate(formValue?.spouseBirthDate) ?? null,
          gender: spouseGender,
          primaryInsuredRelation: 'Conyuge',
          relationship: 'Conyuge',
        } as any,
        isPrimary: false,
      });
    }
    // If family is allowed and children provided, add each with relation 'Child'
    if (
      option?.familyAllowed &&
      formValue?.childrenEnabled &&
      Array.isArray(formValue?.children)
    ) {
      formValue.children.forEach((child: any) => {
        const childGender: Gender | null = (child?.gender as Gender) ?? null;
        insuredPeople.push({
          person: {
            birthDate: toIsoDate(child?.birthDate) ?? null,
            gender: childGender,
            primaryInsuredRelation: 'Child',
            relationship: 'Child',
          } as any,
          isPrimary: false,
        });
      });
    }
    // If family is allowed and others provided, add each with their selected relationship
    if (
      option?.familyAllowed &&
      formValue?.othersEnabled &&
      Array.isArray(formValue?.others)
    ) {
      formValue.others.forEach((other: any) => {
        const otherGender: Gender | null = (other?.gender as Gender) ?? null;
        const rel = other?.relationship || 'Madre';
        insuredPeople.push({
          person: {
            birthDate: toIsoDate(other?.birthDate) ?? null,
            gender: otherGender,
            primaryInsuredRelation: rel,
            relationship: rel,
          } as any,
          isPrimary: false,
        });
      });
    }

    // Build answers using shared mapper
    const answers = mapAnswersFromForm(formValue, existing?.answers ?? []);

    // Resolve selectedPlanId using shared mapper
    const selectedPlanId = resolveSelectedPlanId(option, formValue?.planOption ?? null);

    return {
      typeId,
      selectedPlanId,
      durationYears,
      currency,
      coverages,
      insuredPeople,
      answers,
      planOption: formValue?.planOption ?? null,
    } as QuoteDetailCreateDto;
  }

  private upsertDetail(detail: QuoteDetailCreateDto): void {
    // Enforce single selection
    this.quoteRequest.insuranceTypes = [detail];
    this.selectedTypeIds.clear();
    if (detail.typeId != null) {
      this.selectedTypeIds.add(detail.typeId);
    }
  }

  async onSaveAndContinue(): Promise<void> {
    try {
      // If nothing selected, do nothing
      if (
        !this.quoteRequest.insuranceTypes ||
        this.quoteRequest.insuranceTypes.length === 0
      ) {
        return;
      }

      // Check for special conditions: any insurance type with a "true" answer
      const affectedInsurances = this.getAffectedInsurances();
      if (affectedInsurances.length > 0) {
        // Open special request modal instead of navigating to summary
        this.openSpecialRequestModal(affectedInsurances);
        return;
      }

      // Show loading while creating/updating
      this.swal.showLoading('Guardando cotización...');

      const current = this.quotesService.currentQuote;
      let resp: QuoteResponseDto;

      if (current?.quoteId != null) {
        // Update existing quote when present in session
        const currentDetails = current.details ?? [];

        // Build upsert details with correct IDs for details and coverages
        const upsertDetails: QuoteDetailUpsertDto[] = (
          this.quoteRequest.insuranceTypes ?? []
        ).map((d) => {
          const existing = currentDetails.find((ed) => ed.typeId === d.typeId);
          const existingCoverages = existing?.coverages ?? [];

          const coverages: QuoteCoverageRequestDto[] = (d.coverages ?? []).map(
            (c) => {
              // Find existing quote coverage by matching related coverage id or by name if id is null
              const match = existingCoverages.find(
                (ec) => (c.id != null && (ec.coverage?.id ?? null) === c.id) || (c.id == null && c.name && ec.name === c.name),
              );
              const base: QuoteCoverageRequestDto = {
                coverageId: c.coverageId ?? null,
                coverageCode: c.coverageCode ?? null,
                name: c.name ?? null,
                description: c.description ?? null,
                amount: c.amount,
                currency: c.currency ?? existing?.currency ?? null,
              };
              if (match?.id != null) {
                return { ...base, id: match.id } as QuoteCoverageRequestDto;
              }
              // New quote coverage: do not send id field
              return base;
            },
          );

          const existingIps = existing?.insuredPeople ?? [];
          const existingPrimary = existingIps.find((p: any) => !!p?.isPrimary) || null;
          const existingSpouse = existingIps.find(
            (p: any) => !p?.isPrimary && normalizeRelation((p?.person as any)?.primaryInsuredRelation) === 'spouse',
          ) || null;
          const existingChildren = existingIps.filter(
            (p: any) => !p?.isPrimary && normalizeRelation((p?.person as any)?.primaryInsuredRelation) === 'child',
          );

          let childIdx = 0;
          let otherIdx = 0;
          const existingOthers = existingIps.filter(
            (p: any) => !p?.isPrimary && normalizeRelation((p?.person as any)?.primaryInsuredRelation) === 'other',
          );
          const insuredPeople: QuoteInsuredPersonUpsertDto[] = (d.insuredPeople ?? []).map((ip) => {
            const rel = normalizeRelation((ip?.person as any)?.primaryInsuredRelation);
            let match: any = null;
            if (ip?.isPrimary) match = existingPrimary;
            else if (rel === 'spouse') match = existingSpouse;
            else if (rel === 'child') match = existingChildren[childIdx++] ?? null;
            else if (rel === 'other') match = existingOthers[otherIdx++] ?? null;

            return {
              id: (match as any)?.id ?? null,
              personId: (match as any)?.personId ?? null,
              isPrimary: !!ip?.isPrimary,
              contactEmail: (ip as any)?.contactEmail ?? (match as any)?.contactEmail ?? null,
              homePhone: (ip as any)?.homePhone ?? (match as any)?.homePhone ?? null,
              mobilePhone: (ip as any)?.mobilePhone ?? (match as any)?.mobilePhone ?? null,
              person: (() => {
                const p = { ...(ip?.person as any) };
                if (p.address) {
                  const a = p.address;
                  // If address has no meaningful data, send null to avoid cleanup validation errors
                  const isComplete =
                    a.street &&
                    a.postalCode &&
                    a.countryCode &&
                    a.stateCode &&
                    a.countyCode;
                  if (!isComplete) {
                    p.address = null;
                  }
                }
                return p;
              })(),
            } as QuoteInsuredPersonUpsertDto;
          });

          return {
            id: existing?.id ?? null,
            typeId: d.typeId,
            selectedPlanId: d.selectedPlanId ?? null,
            durationYears: d.durationYears ?? null,
            currency: d.currency ?? existing?.currency ?? null,
            coverages,
            insuredPeople,
          } as QuoteDetailUpsertDto;
        });

        const updateBody: UpdateQuoteRequestDto = {
          quoteEmail:
            this.quoteRequest.quoteEmail ??
            this.quoteRequest.contactEmail ??
            null,
          contactEmail: this.quoteRequest.contactEmail ?? null,
          producerCode: denormalizeProducerCode(this.quoteRequest.producerCode),
          retailer: this.quoteRequest.retailer ?? null,
          supervisor: this.quoteRequest.supervisor ?? null,
          manager: this.quoteRequest.manager ?? null,
          branch: (this.quoteRequest.branch as any) ?? null,
          details: upsertDetails,
        };
        resp = await lastValueFrom(
          this.quotesService.updateQuote(
            current.quoteId as number,
            updateBody as any,
          ),
        );
      } else {
        // Denormalize producerCode for the create request (null → '802')
        const createBody = {
          ...this.quoteRequest,
          producerCode: denormalizeProducerCode(this.quoteRequest.producerCode),
        };
        resp = await lastValueFrom(
          this.quotesService.createQuote(createBody),
        );
      }

      // Navigate to summary screen with quoteId
      const quoteId = (resp as any)?.quoteId ?? null;
      if (quoteId != null) {
        await this.router.navigate(['quote', 'summary'], {
          queryParams: { quoteId },
        });
      } else {
        await this.router.navigate(['quote', 'summary']);
      }
    } catch (e) {
      // Silently fail for now or add feedback
      // Optionally show an error
      // this.swal.error('No se pudo guardar la cotización');
    } finally {
      this.swal.dismiss();
    }
  }

  /**
   * Returns the list of insurance type details that have at least one answer that triggers cancellation/special request.
   */
  private getAffectedInsurances(): AffectedInsurance[] {
    const affected: AffectedInsurance[] = [];
    for (const detail of this.quoteRequest.insuranceTypes ?? []) {
      const typeId = detail.typeId;
      if (typeId == null) continue;

      const answers = this.questionAnswersMap.get(typeId);
      if (!answers) continue;

      const option = this.insuranceOptions.find((o) => o.id === typeId);
      if (!option) continue;

      // Check all questions in Selection phase (or all visible ones?)
      // We check all that have a cancelOnValue defined.
      const groups = option.questionGroups || [];
      const questions = groups.flatMap(g => g.questions || []);

      const affectedQuestions: { text: string; answer: boolean }[] = [];

      for (const q of questions) {
        if (!q.cancelOnValue) continue;

        const key = `question_${q.id}`;
        const answerVal = answers[key];

        // Normalize for comparison
        const answerStr = answerVal === true ? 'true' : answerVal === false ? 'false' : String(answerVal ?? '');
        const targetStr = String(q.cancelOnValue);

        if (answerStr.toLowerCase() === targetStr.toLowerCase()) {
          affectedQuestions.push({
            text: q.title || 'Pregunta de salud',
            answer: true, // This property implies "bad answer found" for the modal
          });
        }
      }

      if (affectedQuestions.length > 0) {
        affected.push({
          name: option.name || 'Seguro',
          questions: affectedQuestions,
        });
      }
    }
    return affected;
  }

  /**
   * Opens the special request modal for insurances with special conditions.
   */
  private openSpecialRequestModal(affectedInsurances: AffectedInsurance[]): void {
    const modalRef = this.modalService.open(SpecialRequestModalComponent, {
      centered: true,
      size: 'md',
      backdrop: 'static',
      keyboard: false,
    });

    const instance = modalRef.componentInstance as SpecialRequestModalComponent;
    instance.affectedInsurances = affectedInsurances;

    modalRef.result
      .then(async (data: SpecialRequestData) => {
        try {
          this.swal.showLoading('Guardando cotización...');

          // Step 1: Save the quote to get a quoteId
          const createBody = {
            ...this.quoteRequest,
            producerCode: denormalizeProducerCode(this.quoteRequest.producerCode),
          };
          const quoteResp = await lastValueFrom(
            this.quotesService.createQuote(createBody),
          );
          const quoteId = (quoteResp as any)?.quoteId;
          if (!quoteId) {
            this.swal.error('No se pudo guardar la cotización. Por favor, intente nuevamente.');
            return;
          }

          // Step 2: Send the special request with quoteId
          this.swal.showLoading('Enviando solicitud...');
          await lastValueFrom(
            this.specialRequestsService.submit({
              nombre: data.nombre,
              cedula: data.cedula,
              email: data.email,
              telefono: data.telefono,
              quoteId,
            }),
          );

          await this.swal.success(
            `Su solicitud ha sido registrada con el No. de cotización: <strong>${quoteId}</strong>.<br>Un representante se comunicará con usted.`,
          );

          // After user closes the success dialog, reset and redirect silently
          this.quotesService.clearCurrentQuote();
          void this.router.navigate(['/']);
        } catch (e) {
          this.swal.error('No se pudo enviar la solicitud. Por favor, intente nuevamente.');
          return;
        }
      })
      .catch(() => {
        // User cancelled, stay on the page
      });
  }



  private async ensurePopovers(): Promise<void> {
    if (typeof window === 'undefined') {
      return;
    }

    const buttons = this.infoButtons ?? [];
    const elements = new Set<HTMLElement>();

    buttons.forEach((buttonRef) => elements.add(buttonRef.nativeElement));

    if (!elements.size) {
      this.detachPopovers();
      return;
    }

    if (!this.popoverCtor) {
      const { default: Popover } = await import('bootstrap/js/dist/popover.js');
      this.popoverCtor = Popover;
    }

    // Dispose popovers that are no longer present
    this.popovers.forEach((popover, element) => {
      if (!elements.has(element)) {
        popover.dispose();
        this.popovers.delete(element);
      }
    });

    // Attach popovers to new elements
    elements.forEach((element) => {
      if (this.popovers.has(element) || !this.popoverCtor) {
        return;
      }

      const title = element.dataset['infoTitle'] ?? '';
      const content = formatDescription(element.dataset['infoContent'] ?? '');

      const popover = new this.popoverCtor(element, {
        trigger: 'focus',
        title,
        content,
        container: 'body',
        html: true,
        customClass: 'custom-popover',
      });

      this.popovers.set(element, popover);
    });
  }

  private detachPopovers(): void {
    this.popovers.forEach((popover) => popover.dispose());
    this.popovers.clear();
  }

  protected readonly getSafeIconName = getSafeIconName;
}
